import React from "react";
import {getRandomNumber} from "./fns";
import {colorsHex, MINT, NEUTRAL, PRIMARY} from "../constants/consts.common";
import Feature, {FeatureLike} from "ol/Feature";
import {StyleLike} from "ol/style/Style";
import {Fill, Icon, Stroke, Style, Text} from "ol/style";
import CircleStyle from "ol/style/Circle";
import {Options as IconOptions} from "ol/style/Icon";
import {renderToStaticMarkup} from "react-dom/server";
import {Marker} from "../assets/svgs/marker";
import {TMarkerProperty} from "shared/hooks/useMapFunctions";

type TColorCache = {
  [key: string]: string
};

type TStyleCache = {
  [key: string]: Style
};

export const CLUSTER_GROUP_KEY = "group_id";
const colorCache: TColorCache = {};
const clusterCache: TStyleCache = {};
const unusedColorsCache = [...colorsHex];
const getRandomColor = (code: string, shouldFilter: boolean = true) => {
  if (!colorCache[code]) {
    const random = getRandomNumber(shouldFilter ? [MINT, PRIMARY] : []);
    // @ts-ignore
    let randomColor = "#" + random.toString(16);
    randomColor = randomColor.padEnd(7, "0");
    colorCache[code] = randomColor;
  }
  return colorCache[code];
};

export const getMarkerColor = (unusedColors: string[], code: string, shouldFilter: boolean = true) => {
  if (!colorCache[code]) {
    // if list is empty → return random color
    if (unusedColors.length === 0) {
      colorCache[code] = getRandomColor(code, shouldFilter);
    }
    else {
      // take one color from the list
      colorCache[code] = unusedColors.shift() as string;
    }
  }
  return colorCache[code];
};

const getMarkerIcon = (color?: string, strokeColor?: string): IconOptions => {
  let fillColor = (color ? color : '#' + PRIMARY), centerColor = `rgba(0, 0, 0, 0.40)`;
  const svgString = renderToStaticMarkup(<Marker fillColor={fillColor} strokeColor={strokeColor || "#fff"}
                                                 centerColor={strokeColor || centerColor} />);
  const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
  return ({
    src: svgDataUrl, scale: 1, anchorOrigin: "bottom-left",
  });
}

function round4(n: number) {
  return Math.round(n * 100000);
}

const getCircleStyleFromRes = (resolution: number = 13) => {
  if (resolution < 2) { // was 130 in EPSG:3857
    return {radius: 16, alpha: 95};
  }
  else if (resolution < 3) { // was 45
    return {radius: 20, alpha: 85};
  }
  else if (resolution < 10) { // was 90
    return {radius: 24, alpha: 75};
  }
  else if (resolution < 20) { // was 90
    return {radius: 27, alpha: 65};
  }
  else if (resolution < 60) { // was 90
    return {radius: 31, alpha: 65};
  }
  else if (resolution < 80) { // was 90
    return {radius: 35, alpha: 55};
  }
  return {radius: 37, alpha: 45};
}

const createClusterStyle = (color: string, size: number, radius: number, alpha: number = 99, strokeColor?: string) => {
  if (size === 1) {
    return new Style({
      image: new Icon(getMarkerIcon(color, strokeColor)),
    });
  }
  else {
    return new Style({
      image: new CircleStyle({
        radius, stroke: new Stroke({
          width: 2, color: strokeColor || '#fff',
        }), fill: new Fill({
          color: color ? `${color}${alpha}` : "#3399CC",
        }),
      }), text: new Text({
        font: "bold 13px Spoqa Han Sans Neo", text: size.toString(), fill: new Fill({
          color: "#fff",
        }),
      }),
    })
  }
}

const isHighlightedOrDisabled = (features: Feature[]) => {
  let highlighted = false;
  let disabled = true;

  let i = 1;
  for (const feature of features) {
    const properties = feature.getProperties() as TMarkerProperty;

    // One selected feature is enough to treat the whole cluster as highlighted.
    if (properties.highlighted) {
      highlighted = true;
    }
    // The cluster is disabled only when every feature is disabled.
    if (!properties.disabled) {
      disabled = false;
    }
    
    if (highlighted && !disabled) {
      break;
    }
  }
  return {disabled, highlighted};
}

export const clusterStyle = (feature: FeatureLike, resolution: number) => {
  const fixedRes = round4(resolution);
  const features = feature.get("features");
  const size = features.length;
  if (size > 0) {
    const {radius, alpha} = getCircleStyleFromRes(fixedRes);
    const {disabled, highlighted} = isHighlightedOrDisabled(features)
    const colorId = features[0].get(CLUSTER_GROUP_KEY);
    if (disabled) {
      const cacheKey = radius + "disabled" + colorId + size;
      if (!clusterCache[cacheKey]) {
        const color = getMarkerColor(unusedColorsCache, colorId);
        clusterCache[cacheKey] = createClusterStyle("#" + NEUTRAL, size, radius, 99, color);
      }
      return clusterCache[cacheKey]
    }
    else if (highlighted) {
      const key = "highlighted" + radius + size;
      if (!clusterCache[key]) {
        clusterCache[key] = createClusterStyle("#" + MINT, size, radius);
      }
      return clusterCache[key]
    }
    const cacheKey = radius + colorId + size;
    if (!clusterCache[cacheKey]) {
      const color = getMarkerColor(unusedColorsCache, colorId);
      clusterCache[cacheKey] = createClusterStyle(color, size, radius, alpha);
    }
    return clusterCache[cacheKey];
  }
};

export const clusterOverStyle = (feature: FeatureLike, resolution: number) => {
  const fixedRes = round4(resolution);
  const size = feature.get("features").length;
  const color = "#" + PRIMARY;
  const {radius} = getCircleStyleFromRes(fixedRes);
  const cacheKey = color + radius + size;
  if (!clusterCache[cacheKey]) {
    clusterCache[cacheKey] = createClusterStyle(color, size, radius);
  }
  return clusterCache[cacheKey];
};
